/**
 * State management for the Reports › Transactions tab. Composed into
 * `ReportsController` through `Inherit` next to `ReportsTransactionsController`
 * — the same controller/delegate stacking the rest of the codebase uses.
 *
 * Nothing here talks to the data layer; that is the controller's job. The
 * change handlers stage the new filter model in state and, when the caller
 * hands them an `apply` callback, pass it the staged model — the view decides
 * whether a change applies at once (desktop chips) or waits for the modal's
 * Filter button (small screens), and the controller owns the fetch behind
 * `apply`. A boolean here would have this file calling the fetch itself.
 */
class ReportsTransactionsDelegate {
    /**
     * A filter chip changed. With `apply`, the staged model is handed to it
     * (the controller's `applyTransactionFilters`, which mirrors the filters
     * into the URL and re-fetches at page 1); without it the change is only
     * staged — the small-screen modal applies on its Filter button.
     *
     * @param {string} name a DEFAULT_TRANSACTION_FILTERS key
     * @param {unknown} value
     * @param {{apply?: ((filters: object) => Promise<void> | void) | null}} [options]
     */
    async handleTransactionFilterChange(name, value, { apply = null } = {}) {
        const nextFilters = { ...this.state.transactionFilters, [name]: value };
        this.setState({ transactionFilters: nextFilters });
        if (apply) await apply(nextFilters);
    }

    /**
     * Time frame chip (all / 7d / 30d / custom). A custom frame waits for both
     * ends of the range before it applies; any other frame drops the range.
     *
     * @param {string} timeFrame one of TRANSACTION_TIME_FRAMES
     * @param {{apply?: ((filters: object) => Promise<void> | void) | null}} [options]
     */
    async handleTransactionTimeFrameChange(timeFrame, { apply = null } = {}) {
        const dateRange = timeFrame === 'custom' ? (this.state.transactionFilters?.dateRange ?? null) : null;
        const nextFilters = { ...this.state.transactionFilters, timeFrame, dateRange };
        this.setState({ transactionFilters: nextFilters });
        const complete = timeFrame !== 'custom' || (dateRange?.[0] && dateRange?.[1]);
        if (apply && complete) await apply(nextFilters);
    }

    /**
     * The custom range picker changed. Picking both ends applies immediately;
     * clearing one end only stages.
     *
     * @param {Array|null} dateRange `[dayjs, dayjs]` or null
     * @param {{apply?: ((filters: object) => Promise<void> | void) | null}} [options]
     */
    async handleTransactionDateRangeChange(dateRange, { apply = null } = {}) {
        const nextFilters = { ...this.state.transactionFilters, timeFrame: 'custom', dateRange: dateRange || null };
        this.setState({ transactionFilters: nextFilters });
        if (apply && dateRange?.[0] && dateRange?.[1]) await apply(nextFilters);
    }
}

export default ReportsTransactionsDelegate;
